/**
 * Public invite-signup page for beta testers.
 *
 * Route: /signup?invite=<token>
 *
 * Flow: create a Firebase Auth account (or sign in to an existing one), then
 * call the `hugoClaimBetaInvite` callable Cloud Function which validates the
 * token server-side and attaches the user to their organization. No client
 * reads of Hugo_BetaInvites — all invite validation happens in the function.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app, { auth } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

// Match the function's deployed region (same pattern as hugoCreateOrgUser).
const functions = getFunctions(app, 'us-central1');

/** Shared page shell: centered REPSit-branded card, mirrors LoginPage. */
function SignupShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 420, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box
            component="img"
            src="/repsit-logo.png"
            alt="REPSit"
            sx={{ display: 'block', mx: 'auto', height: 96, mb: 1 }}
          />
          <Typography
            variant="body2"
            align="center"
            color="text.secondary"
            sx={{ mb: 4 }}
          >
            AI-Powered Performance Intelligence
          </Typography>
          {children}
        </CardContent>
      </Card>
    </Box>
  );
}

function Signup() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('invite');
  // Invite links include the invitee's email (added by create-invites.cjs) so
  // the form arrives prefilled — still editable in case they prefer another.
  const invitedEmail = searchParams.get('email') || '';
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [email, setEmail] = useState(invitedEmail);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null); // { organizationName }

  /** Step 2: redeem the invite token (user must be signed in by now). */
  const claimInvite = async () => {
    const callable = httpsCallable(functions, 'hugoClaimBetaInvite');
    const res = await callable({ token });
    const data = res.data || {};
    setSuccess({ organizationName: data.organizationName || 'your organization' });
  };

  /** Full form flow: create account (or recover a half-finished signup), then claim. */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return; // guard against double-submit
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      try {
        await createUserWithEmailAndPassword(auth, email, password);
      } catch (err) {
        if (err.code === 'auth/email-already-in-use') {
          // The tester may be retrying after a half-finished signup — try
          // signing in with the same credentials before giving up.
          try {
            await signInWithEmailAndPassword(auth, email, password);
          } catch (signInErr) {
            setError(
              'This email already has an account — sign in at /login, then reopen your invite link.'
            );
            setLoading(false);
            return;
          }
        } else if (err.code === 'auth/invalid-email') {
          setError('Please enter a valid email address.');
          setLoading(false);
          return;
        } else if (err.code === 'auth/weak-password') {
          setError('Password is too weak. Please choose a stronger password.');
          setLoading(false);
          return;
        } else if (err.code === 'auth/network-request-failed') {
          setError('Network error. Please check your connection and try again.');
          setLoading(false);
          return;
        } else {
          setError('Could not create your account. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Signed in — now redeem the invite.
      await claimInvite();
    } catch (err) {
      // Cloud Function errors carry user-friendly messages — show them verbatim.
      setError(err.message || 'Something went wrong claiming your invite. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /** Already-signed-in flow: skip account creation, claim directly. */
  const handleClaimAsCurrentUser = async () => {
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      await claimInvite();
    } catch (err) {
      setError(err.message || 'Something went wrong claiming your invite. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUseDifferentAccount = async () => {
    setError('');
    try {
      await logout();
    } catch (err) {
      // Ignore — worst case the user stays signed in and can retry.
    }
  };

  // --- No invite token in the URL ---
  if (!token) {
    return (
      <SignupShell>
        <Typography variant="h6" align="center" sx={{ mb: 2 }}>
          Invite required
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          This page is for invited beta testers. If you have an invite email, use
          the link inside it.
        </Typography>
        <Box sx={{ textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" variant="body2">
            Go to Login
          </Link>
        </Box>
      </SignupShell>
    );
  }

  // --- Invite claimed successfully ---
  if (success) {
    return (
      <SignupShell>
        <Alert severity="success" sx={{ mb: 2 }}>
          Welcome to REPSit.ai — you&rsquo;re in.
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          We&rsquo;ve set you up with <strong>{success.organizationName}</strong> — a fictional
          practice company preloaded with realistic data, so you can explore and break things
          freely. Nothing here touches a real organization. When you&rsquo;re ready, you can
          import your own org chart from Settings → Organization.
        </Typography>
        <Button
          variant="contained"
          fullWidth
          size="large"
          sx={{ py: 1.5 }}
          onClick={() => navigate('/dashboard')}
        >
          Enter REPSit.ai
        </Button>
      </SignupShell>
    );
  }

  // --- Already signed in (and not mid-form-submit): claim with this account ---
  if (user && !loading) {
    return (
      <SignupShell>
        <Typography variant="h6" align="center" sx={{ mb: 2 }}>
          Claim your beta seat
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Signed in as <strong>{user.email}</strong>
        </Typography>
        <Button
          variant="contained"
          fullWidth
          size="large"
          disabled={loading}
          sx={{ py: 1.5, mb: 2 }}
          onClick={handleClaimAsCurrentUser}
        >
          Claim invite with this account
        </Button>
        <Box sx={{ textAlign: 'center' }}>
          <Link
            component="button"
            variant="body2"
            onClick={handleUseDifferentAccount}
            sx={{ cursor: 'pointer' }}
          >
            Use a different account
          </Link>
        </Box>
      </SignupShell>
    );
  }

  // --- Default: signup form ---
  return (
    <SignupShell>
      <Typography variant="h6" align="center" sx={{ mb: 3 }}>
        Claim your beta seat
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <TextField
          label="Email"
          type="email"
          fullWidth
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mb: 2 }}
          autoComplete="email"
          aria-label="Email address"
        />
        <TextField
          label="Password"
          type="password"
          fullWidth
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          sx={{ mb: 2 }}
          autoComplete="new-password"
          aria-label="Password"
          helperText="At least 8 characters"
        />
        <TextField
          label="Confirm password"
          type="password"
          fullWidth
          required
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          sx={{ mb: 3 }}
          autoComplete="new-password"
          aria-label="Confirm password"
        />
        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading}
          sx={{ py: 1.5, mb: 2 }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Create my account'}
        </Button>
        <Box sx={{ textAlign: 'center' }}>
          <Link component={RouterLink} to="/login" variant="body2">
            Already have an account? Log in
          </Link>
        </Box>
      </form>
    </SignupShell>
  );
}

export default Signup;
